// Mind2Magic CRM — Itinerary UI Module (Magic Planner 1.5)
// Renders "Itinerario Premium" section in lead panel.
// Config modal for park selection, DOCX generation trigger, upload zone, version history.

import { store } from '../store.js'
import { generateItineraryDocx } from './docx-generator.js'
import { downloadBlob, extractFamilyName } from './itinerary-helpers.js'
import { classifyHotel, PARK_ENTERTAINMENT } from './content-blocks.js'

// ── LEAD PANEL SECTION ───────────────────────────────────────────────────
// Called from leads.js in both modoCompleto and modoCompacto panels

function renderItinerarySection(l) {
  // Only show for park-related trip types
  const validTypes = ['disney', 'universal', 'disney_universal', 'parques_crucero']
  if (!validTypes.includes(l.type) && !hasParks(l)) return ''

  const versions = l.formData?._itineraryVersions || []
  const latestVersion = versions[versions.length - 1]

  // State machine: no_generado → docx_generado → docx_editado → pdf_listo
  if (!latestVersion) {
    // No generado
    return `
      <div class="panel-section" style="text-align:center;">
        <div style="font-size:11px;color:var(--text-3);margin-bottom:6px;">Itinerario Premium</div>
        <button class="btn btn-sm" onclick="openItineraryConfig('${l.id}')"
          style="width:100%;background:linear-gradient(135deg,var(--purple),var(--blue));color:#fff;">
          📝 Generar Itinerario DOCX
        </button>
      </div>`
  }

  const state = latestVersion.type
  const versionNum = latestVersion.version || versions.length
  const created = latestVersion.createdAt ? new Date(latestVersion.createdAt).toLocaleDateString('es-CO') : ''

  if (state === 'generated') {
    // Check if there's a previously uploaded edited version (e.g., user regenerated DOCX after editing)
    const hasEdited = versions.some(v => v.type === 'edited' && v.rawHtml)
    return `
      <div class="panel-section">
        <div style="font-size:11px;color:var(--text-3);margin-bottom:6px;">Itinerario Premium <span style="color:var(--blue);">v${versionNum} — DOCX generado</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-sm btn-secondary" onclick="redownloadDocx('${l.id}')" style="flex:1;">Re-descargar DOCX</button>
          <button class="btn btn-sm" onclick="showUploadDocx('${l.id}')" style="flex:1;background:var(--purple);color:#fff;">Subir DOCX editado</button>
        </div>
        <div style="display:flex;gap:6px;margin-top:6px;">
          <button class="btn btn-sm" onclick="generateDirectPdf('${l.id}')" style="flex:1;background:linear-gradient(135deg,var(--green),#2ea043);color:#fff;font-size:11px;">${hasEdited ? 'Generar PDF (desde DOCX editado)' : 'Generar PDF Directo'}</button>
        </div>
        <div style="font-size:10px;color:var(--text-3);margin-top:4px;text-align:center;">Generado: ${created}</div>
      </div>`
  }

  if (state === 'edited') {
    return `
      <div class="panel-section">
        <div style="font-size:11px;color:var(--text-3);margin-bottom:6px;">Itinerario Premium <span style="color:var(--yellow);">v${versionNum} — DOCX editado</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-sm btn-secondary" onclick="viewItineraryDiff('${l.id}')" style="flex:1;">🔍 Ver cambios</button>
          <button class="btn btn-sm" onclick="generateFinalPdf('${l.id}')" style="flex:1;background:var(--green);color:#fff;">📄 Generar PDF Final</button>
        </div>
      </div>`
  }

  if (state === 'final_pdf') {
    return `
      <div class="panel-section">
        <div style="font-size:11px;color:var(--text-3);margin-bottom:6px;">Itinerario Premium <span style="color:var(--green);">v${versionNum} — PDF listo</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-sm" onclick="downloadFinalPdf('${l.id}')" style="flex:1;background:var(--green);color:#fff;">📥 Descargar PDF</button>
          <button class="btn btn-sm btn-secondary" onclick="sendItineraryWhatsApp('${l.id}')" style="flex:1;">💬 WhatsApp</button>
        </div>
        <div style="display:flex;gap:6px;margin-top:6px;">
          <button class="btn btn-sm btn-ghost" onclick="openItineraryConfig('${l.id}')" style="flex:1;font-size:10px;">🔄 Regenerar</button>
          <button class="btn btn-sm btn-ghost" onclick="showItineraryHistory('${l.id}')" style="flex:1;font-size:10px;">📋 Historial (${versions.length})</button>
        </div>
      </div>`
  }

  return ''
}

// ── CONFIG MODAL ─────────────────────────────────────────────────────────
// Sara selects parks, dates, meal plan toggle before generating

function openItineraryConfig(leadId) {
  const lead = findLead(leadId)
  if (!lead) { window.toast('Lead no encontrado', 'error'); return }

  // Use cached recommendation from "Días Recomendados" if available
  const rec = lead._parkRecommendation || null
  const parks = rec ? detectParksFromRecommendation(rec, lead) : detectParksFromLead(lead)
  const hasDis = parks.some(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
  const hasUni = parks.some(p => ['uso', 'ioa', 'epic'].includes(p))

  const modal = document.getElementById('itineraryConfigModal')
  if (!modal) {
    // Create modal if first time
    createConfigModal()
  }

  // Build schedule from recommendation or lead dates
  const defaultSchedule = rec ? buildScheduleFromRecommendation(rec, lead) : buildScheduleFromLead(lead, parks)
  // Cache recommendation park hours if available
  window._itParkHours = rec || null

  // Populate modal
  const body = document.getElementById('itConfigBody')
  body.innerHTML = `
    <div style="margin-bottom:16px;">
      <div style="font-weight:600;margin-bottom:8px;">Parques a incluir:</div>
      ${hasDis ? `
        <div style="margin-bottom:8px;font-size:12px;color:var(--blue);font-weight:600;">Disney World</div>
        <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;">
          <input type="checkbox" class="it-park-check" value="mk" ${parks.includes('mk') ? 'checked' : ''} onchange="updateItSchedule('${leadId}')"> Magic Kingdom
        </label>
        <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;">
          <input type="checkbox" class="it-park-check" value="hs" ${parks.includes('hs') ? 'checked' : ''} onchange="updateItSchedule('${leadId}')"> Hollywood Studios
        </label>
        <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;">
          <input type="checkbox" class="it-park-check" value="ep" ${parks.includes('ep') ? 'checked' : ''} onchange="updateItSchedule('${leadId}')"> EPCOT
        </label>
        <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;">
          <input type="checkbox" class="it-park-check" value="ak" ${parks.includes('ak') ? 'checked' : ''} onchange="updateItSchedule('${leadId}')"> Animal Kingdom
        </label>
      ` : ''}
      ${hasUni ? `
        <div style="margin-bottom:8px;margin-top:12px;font-size:12px;color:var(--green);font-weight:600;">Universal Orlando</div>
        <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;">
          <input type="checkbox" class="it-park-check" value="uso" ${parks.includes('uso') ? 'checked' : ''} onchange="updateItSchedule('${leadId}')"> Universal Studios
        </label>
        <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;">
          <input type="checkbox" class="it-park-check" value="ioa" ${parks.includes('ioa') ? 'checked' : ''} onchange="updateItSchedule('${leadId}')"> Islands of Adventure
        </label>
        <label style="display:flex;align-items:center;gap:6px;margin-bottom:4px;cursor:pointer;">
          <input type="checkbox" class="it-park-check" value="epic" ${parks.includes('epic') ? 'checked' : ''} onchange="updateItSchedule('${leadId}')"> Epic Universe
        </label>
      ` : ''}
    </div>

    <div style="margin-bottom:16px;">
      <div style="font-weight:600;margin-bottom:8px;">Fechas del viaje:</div>
      <div style="display:flex;gap:12px;margin-bottom:12px;">
        <div style="flex:1;">
          <label style="font-size:11px;color:var(--text-3);display:block;margin-bottom:4px;">Check-in</label>
          <input type="date" id="itCheckInDate" value="${lead.hotels?.[0]?.checkIn || lead.travelDate || ''}" style="font-size:12px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);width:100%;">
        </div>
        <div style="flex:1;">
          <label style="font-size:11px;color:var(--text-3);display:block;margin-bottom:4px;">Check-out</label>
          <input type="date" id="itCheckOutDate" value="${lead.hotels?.[0]?.checkOut || ''}" style="font-size:12px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);width:100%;">
        </div>
      </div>
    </div>

    <div style="margin-bottom:16px;">
      <div style="font-weight:600;margin-bottom:8px;">Agenda del viaje:</div>
      <div style="font-size:11px;color:var(--text-3);margin-bottom:8px;">Asigna un parque a cada día. Puedes cambiar las fechas y el orden.</div>
      <div id="itScheduleRows">${renderScheduleRows(defaultSchedule, parks)}</div>
      <button class="btn btn-sm btn-ghost" onclick="addItScheduleRow('${leadId}')" style="font-size:11px;margin-top:6px;">+ Agregar día</button>
    </div>

    <div style="margin-bottom:16px;">
      <div style="font-weight:600;margin-bottom:8px;">Plan de Comidas:</div>
      <select id="itDiningPlan" style="font-size:13px;padding:6px 10px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);width:100%;">
        <option value="">Sin Plan de Comidas</option>
        <option value="quick_service" ${detectDiningTypeFromLead(lead) === 'quick_service' ? 'selected' : ''}>Quick Service Dining Plan</option>
        <option value="standard" ${detectDiningTypeFromLead(lead) === 'standard' ? 'selected' : ''}>Disney Dining Plan</option>
      </select>
    </div>

    <div class="itinerary-section" style="margin-top: 16px;">
      <label style="font-weight: 600; display: block; margin-bottom: 8px;">Reservaciones de restaurante</label>
      <div id="dining-reservations-rows"></div>
      <button type="button" onclick="window._addDiningRow()" style="margin-top: 4px; font-size: 12px; cursor: pointer;">+ Agregar reservacion</button>
    </div>

    <div class="itinerary-section" style="margin-top: 16px;">
      <label style="font-weight: 600; display: block; margin-bottom: 8px;">Turnos de Lightning Lane</label>
      <div id="ll-turn-rows"></div>
      <button type="button" onclick="window._addLLRow()" style="margin-top: 4px; font-size: 12px; cursor: pointer;">+ Agregar turno</button>
    </div>

    <div class="itinerary-section" style="margin-top: 16px;">
      <label style="font-weight: 600; display: block; margin-bottom: 8px;">Planes de la noche (despues del parque)</label>
      <div id="evening-plan-rows"></div>
      <button type="button" onclick="window._addEveningRow()" style="margin-top: 4px; font-size: 12px; cursor: pointer;">+ Agregar plan</button>
    </div>

    <div style="margin-bottom:16px;">
      <div style="font-weight:600;margin-bottom:8px;">Horarios de parque (opcional):</div>
      <div style="font-size:11px;color:var(--text-3);margin-bottom:8px;">Se pueden llenar automáticamente o dejar en blanco para que Sara los agregue.</div>
      <button class="btn btn-sm btn-secondary" onclick="fetchParkHours('${leadId}')" style="font-size:11px;">
        🔄 ${rec ? 'Actualizar horarios' : 'Obtener horarios automáticos'}
      </button>
      <div id="itParkHoursStatus" style="font-size:10px;margin-top:4px;${rec ? 'color:var(--green);' : 'color:var(--text-3);'}">${rec ? '✅ Datos de "Días Recomendados" pre-cargados' : ''}</div>
    </div>

    <button class="btn" onclick="confirmGenerateDocx('${leadId}')"
      style="width:100%;background:linear-gradient(135deg,var(--purple),var(--blue));color:#fff;padding:12px;font-size:14px;">
      📝 Generar Itinerario DOCX
    </button>
  `

  window.openModal('itineraryConfigModal')
}

function createConfigModal() {
  const div = document.createElement('div')
  div.id = 'itineraryConfigModal'
  div.className = 'modal-bg'
  div.innerHTML = `
    <div class="modal" style="max-width:480px;">
      <div class="modal-head">
        <h2 class="modal-title">Configurar Itinerario Premium</h2>
        <button class="modal-close" onclick="closeModal('itineraryConfigModal')">✕</button>
      </div>
      <div class="modal-body" id="itConfigBody"></div>
    </div>
  `
  document.body.appendChild(div)
  div.addEventListener('click', (e) => {
    if (e.target === div) window.closeModal('itineraryConfigModal')
  })
}

// ── DOCX UPLOAD ZONE ─────────────────────────────────────────────────────

function showUploadDocx(leadId) {
  const modal = document.getElementById('itUploadModal')
  if (!modal) createUploadModal()

  document.getElementById('itUploadLeadId').value = leadId
  document.getElementById('itUploadStatus').textContent = ''
  document.getElementById('itUploadZone').classList.remove('uploading')

  window.openModal('itUploadModal')
}

function createUploadModal() {
  const div = document.createElement('div')
  div.id = 'itUploadModal'
  div.className = 'modal-bg'
  div.innerHTML = `
    <div class="modal" style="max-width:480px;">
      <div class="modal-head">
        <h2 class="modal-title">Subir DOCX Editado</h2>
        <button class="modal-close" onclick="closeModal('itUploadModal')">✕</button>
      </div>
      <div class="modal-body">
        <input type="hidden" id="itUploadLeadId">
        <div id="itUploadZone" style="border:2px dashed var(--border);border-radius:var(--radius);padding:40px 20px;text-align:center;cursor:pointer;transition:all .2s;"
          ondragover="event.preventDefault();this.style.borderColor='var(--purple)';this.style.background='var(--bg-2)'"
          ondragleave="this.style.borderColor='var(--border)';this.style.background=''"
          ondrop="handleDocxDrop(event)"
          onclick="document.getElementById('itDocxFileInput').click()">
          <div style="font-size:32px;margin-bottom:8px;">📄</div>
          <div style="font-weight:600;">Arrastra tu DOCX editado aquí</div>
          <div style="font-size:12px;color:var(--text-3);margin-top:4px;">o haz clic para seleccionar archivo</div>
        </div>
        <input type="file" id="itDocxFileInput" accept=".docx" style="display:none" onchange="handleDocxSelect(event)">
        <div id="itUploadStatus" style="font-size:12px;color:var(--text-3);margin-top:8px;text-align:center;"></div>
      </div>
    </div>
  `
  document.body.appendChild(div)
  div.addEventListener('click', (e) => {
    if (e.target === div) window.closeModal('itUploadModal')
  })
}

function handleDocxDrop(event) {
  event.preventDefault()
  event.currentTarget.style.borderColor = 'var(--border)'
  event.currentTarget.style.background = ''
  const file = event.dataTransfer?.files?.[0]
  if (file) processUploadedDocx(file)
}

function handleDocxSelect(event) {
  const file = event.target?.files?.[0]
  if (file) processUploadedDocx(file)
}

async function processUploadedDocx(file) {
  if (!file.name.endsWith('.docx')) {
    window.toast('Solo se aceptan archivos .docx', 'error')
    return
  }

  const leadId = document.getElementById('itUploadLeadId')?.value
  if (!leadId) return

  const statusEl = document.getElementById('itUploadStatus')
  statusEl.textContent = 'Procesando DOCX...'

  try {
    // Lazy-load the parser (will be in docx-parser.js)
    const { parseUploadedDocx } = await import('./docx-parser.js')
    const result = await parseUploadedDocx(file, leadId)

    if (result.success) {
      window.closeModal('itUploadModal')
      window.toast(`DOCX procesado — ${result.changesCount} cambios detectados`, 'success')

      // Store edited version
      const lead = findLead(leadId)
      if (lead) {
        if (!lead.formData) lead.formData = {}
        if (!lead.formData._itineraryVersions) lead.formData._itineraryVersions = []
        lead.formData._itineraryVersions.push({
          version: lead.formData._itineraryVersions.length + 1,
          type: 'edited',
          sections: result.sections,
          changes: result.changes,
          rawHtml: result.rawHtml || '',
          createdAt: new Date().toISOString(),
        })
        window.syncLeadToSupabase?.(lead)
        window.renderAll?.()
      }
    } else {
      statusEl.textContent = `Error: ${result.error}`
    }
  } catch (err) {
    statusEl.textContent = `Error procesando archivo: ${err.message}`
    console.error('[Itinerary] Upload error:', err)
  }
}

// ── FAMILY NAME PROMPT ────────────────────────────────────────────────────
// Shows a small modal to confirm/edit the family name before DOCX generation.
// Pre-fills with extractFamilyName(lead), user can change it.

function promptFamilyName(lead, callback) {
  const defaultName = extractFamilyName(lead)

  // Create prompt modal if it doesn't exist
  let modal = document.getElementById('itFamilyNameModal')
  if (!modal) {
    modal = document.createElement('div')
    modal.id = 'itFamilyNameModal'
    modal.className = 'modal-bg'
    modal.innerHTML = `
      <div class="modal" style="max-width:380px;">
        <div class="modal-head">
          <h2 class="modal-title">Nombre de la Familia</h2>
          <button class="modal-close" onclick="closeModal('itFamilyNameModal')">✕</button>
        </div>
        <div class="modal-body" style="padding:20px;">
          <div style="font-size:12px;color:var(--text-3);margin-bottom:10px;">Este nombre aparecerá en el saludo y en el archivo del itinerario.</div>
          <input type="text" id="itFamilyNameInput"
            style="width:100%;font-size:15px;padding:10px 12px;border:1px solid var(--border);border-radius:6px;background:var(--bg-2);color:var(--text-1);box-sizing:border-box;"
            placeholder="Apellido de la familia">
          <button id="itFamilyNameConfirm" class="btn"
            style="width:100%;margin-top:14px;background:linear-gradient(135deg,var(--purple),var(--blue));color:#fff;padding:12px;font-size:14px;">
            📝 Generar DOCX
          </button>
        </div>
      </div>
    `
    document.body.appendChild(modal)
    modal.addEventListener('click', (e) => {
      if (e.target === modal) window.closeModal('itFamilyNameModal')
    })
  }

  const input = document.getElementById('itFamilyNameInput')
  input.value = defaultName

  // Wire up confirm button
  const btn = document.getElementById('itFamilyNameConfirm')
  const handler = () => {
    const name = input.value.trim() || defaultName
    window.closeModal('itFamilyNameModal')
    btn.removeEventListener('click', handler)
    callback(name)
  }
  // Remove any old listener by replacing the button
  const newBtn = btn.cloneNode(true)
  btn.parentNode.replaceChild(newBtn, btn)
  newBtn.addEventListener('click', handler)

  // Also allow Enter key
  input.onkeydown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handler() }
  }

  window.openModal('itFamilyNameModal')
  setTimeout(() => { input.select(); input.focus() }, 100)
}

// ── ENTERTAINMENT PREVIEW/EDIT MODAL ─────────────────────────────────────
// Shows entertainment times from 3 sources before DOCX generation.
// Sara can adjust times manually before exporting.

const PARK_COLORS = {
  mk: '#1565C0', hs: '#C62828', ep: '#6A1B9A', ak: '#2E7D32',
  uso: '#1565C0', ioa: '#00838F', epic: '#E65100',
}
const PARK_NAMES_SHORT = {
  mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom',
  uso: 'Universal Studios', ioa: 'Islands of Adventure', epic: 'Epic Universe',
}
const SOURCE_LABELS = {
  disney_finder: 'Disney API',
  themeparks_wiki: 'ThemeParks.wiki',
  static: 'Datos base',
  manual: 'Manual',
}
const SOURCE_COLORS = {
  disney_finder: '#1565C0',
  themeparks_wiki: '#2E7D32',
  static: '#757575',
  manual: '#6A1B9A',
}

function buildEntertainmentPreviewData(config) {
  // Merge entertainment from all 3 sources into a unified per-park-day structure
  // Priority: Disney Finder API (per-date) > ThemeParks.wiki (live/flat) > Static
  const schedule = config.parkSchedule || []
  const result = {} // { parkCode: [{ name, times, type, source, sara }] }

  for (const day of schedule) {
    const pc = day.parkCode
    if (result[pc]) continue // already built for this park

    const shows = []
    // 1. Disney Finder API (per-date format)
    const dateEnt = config.entertainment?.[day.date]?.[pc]
    // 2. Flat/live format
    const flatEnt = config.entertainment?.[pc]
    // 3. ThemeParks.wiki live
    const liveEnt = config.entertainmentLive?.[pc]
    // 4. Static
    const staticEnt = PARK_ENTERTAINMENT[pc] || []

    // Build from best source first, merge static comments
    const apiShows = dateEnt || flatEnt || liveEnt || null
    const source = dateEnt ? 'disney_finder' : (flatEnt ? 'disney_finder' : (liveEnt ? 'themeparks_wiki' : 'static'))

    if (apiShows && Array.isArray(apiShows)) {
      for (const show of apiShows) {
        // Find matching static entry for Sara's comment
        const staticMatch = staticEnt.find(s => fuzzyNameMatch(s.name, show.name))
        shows.push({
          name: show.name,
          times: Array.isArray(show.times) ? show.times.join(', ') : (show.hours || show.times || ''),
          type: show.type || 'show',
          source,
          sara: staticMatch?.sara || '',
          location: staticMatch?.location || show.location || '',
        })
      }
      // Add static shows not in API (may have been missed)
      for (const s of staticEnt) {
        if (!shows.find(sh => fuzzyNameMatch(sh.name, s.name))) {
          shows.push({
            name: s.name,
            times: s.times || '',
            type: 'show',
            source: 'static',
            sara: s.sara || '',
            location: s.location || '',
          })
        }
      }
    } else {
      // Only static data
      for (const s of staticEnt) {
        shows.push({
          name: s.name,
          times: s.times || '',
          type: 'show',
          source: 'static',
          sara: s.sara || '',
          location: s.location || '',
        })
      }
    }

    result[pc] = shows
  }

  return result
}

function fuzzyNameMatch(a, b) {
  if (!a || !b) return false
  const normalize = s => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const na = normalize(a)
  const nb = normalize(b)
  return na.includes(nb) || nb.includes(na)
}

function createEntertainmentPreviewModal() {
  const div = document.createElement('div')
  div.id = 'itEntertainmentModal'
  div.className = 'modal-bg'
  div.innerHTML = `
    <div class="modal" style="max-width:640px;max-height:85vh;display:flex;flex-direction:column;">
      <div class="modal-head">
        <h2 class="modal-title">Horarios de Entretenimiento</h2>
        <button class="modal-close" onclick="closeModal('itEntertainmentModal')">&#x2715;</button>
      </div>
      <div class="modal-body" id="itEntBody" style="overflow-y:auto;flex:1;padding:16px;"></div>
    </div>
  `
  document.body.appendChild(div)
  div.addEventListener('click', (e) => {
    if (e.target === div) window.closeModal('itEntertainmentModal')
  })
}

function showEntertainmentPreview(config, lead, onConfirm) {
  let modal = document.getElementById('itEntertainmentModal')
  if (!modal) {
    createEntertainmentPreviewModal()
    modal = document.getElementById('itEntertainmentModal')
  }

  const previewData = buildEntertainmentPreviewData(config)
  window._itEntPreviewData = previewData  // Set BEFORE rendering rows
  const body = document.getElementById('itEntBody')
  const schedule = config.parkSchedule || []

  // Build legend
  let legendHTML = '<div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:14px;font-size:11px;">'
  for (const [key, label] of Object.entries(SOURCE_LABELS)) {
    legendHTML += `<span style="display:flex;align-items:center;gap:4px;">
      <span style="width:8px;height:8px;border-radius:50%;background:${SOURCE_COLORS[key]};display:inline-block;"></span>
      ${label}
    </span>`
  }
  legendHTML += '</div>'

  // Build park sections (ordered by schedule)
  const seenParks = new Set()
  let sectionsHTML = ''

  for (const day of schedule) {
    const pc = day.parkCode
    if (seenParks.has(pc)) continue
    seenParks.add(pc)

    const shows = previewData[pc] || []
    const parkColor = PARK_COLORS[pc] || '#666'
    const parkName = PARK_NAMES_SHORT[pc] || pc
    const dateInfo = day.date ? ` — ${day.dateLabel || day.date}` : ''

    sectionsHTML += `
      <div style="margin-bottom:18px;">
        <div style="background:${parkColor};color:#fff;padding:8px 12px;border-radius:6px 6px 0 0;font-weight:600;font-size:13px;">
          ${parkName}${dateInfo}
        </div>
        <div style="border:1px solid var(--border);border-top:none;border-radius:0 0 6px 6px;overflow:hidden;">
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead>
              <tr style="background:var(--bg-2);">
                <th style="text-align:left;padding:6px 10px;font-weight:600;width:35%;">Entretenimiento</th>
                <th style="text-align:left;padding:6px 10px;font-weight:600;width:30%;">Horario</th>
                <th style="text-align:left;padding:6px 10px;font-weight:600;width:10%;">Fuente</th>
                <th style="text-align:center;padding:6px 10px;font-weight:600;width:5%;"></th>
              </tr>
            </thead>
            <tbody>
              ${renderEntParkRows(pc)}
            </tbody>
          </table>
          <div style="padding:6px 10px;border-top:1px solid var(--border);">
            <button onclick="addEntShow('${pc}')" class="btn btn-sm btn-ghost" style="font-size:11px;">+ Agregar show</button>
          </div>
        </div>
      </div>`
  }

  body.innerHTML = `
    <div style="font-size:12px;color:var(--text-3);margin-bottom:12px;">
      Revisa los horarios de shows, desfiles y fuegos artificiales. Puedes editar los horarios manualmente antes de generar el DOCX.
    </div>
    ${legendHTML}
    ${sectionsHTML}
    <button class="btn" id="itEntConfirmBtn"
      style="width:100%;background:linear-gradient(135deg,var(--purple),var(--blue));color:#fff;padding:12px;font-size:14px;margin-top:8px;">
      Continuar con estos horarios
    </button>
  `

  // Wire confirm
  document.getElementById('itEntConfirmBtn').onclick = () => {
    // Collect edited names and times from inputs
    const nameInputs = document.querySelectorAll('.it-ent-name')
    for (const inp of nameInputs) {
      const pc = inp.dataset.park
      const idx = parseInt(inp.dataset.idx)
      if (window._itEntPreviewData[pc]?.[idx]) {
        window._itEntPreviewData[pc][idx].name = inp.value.trim() || 'Show'
      }
    }
    const timeInputs = document.querySelectorAll('.it-ent-time')
    for (const inp of timeInputs) {
      const pc = inp.dataset.park
      const idx = parseInt(inp.dataset.idx)
      if (window._itEntPreviewData[pc]?.[idx]) {
        const oldTimes = window._itEntPreviewData[pc][idx].times
        const newTimes = inp.value.trim()
        if (newTimes !== oldTimes) {
          window._itEntPreviewData[pc][idx].times = newTimes
          window._itEntPreviewData[pc][idx].source = 'manual'
        }
      }
    }

    // Convert preview data back to config.entertainmentLive format for DOCX generator
    const mergedEntertainment = {}
    for (const [pc, shows] of Object.entries(window._itEntPreviewData)) {
      mergedEntertainment[pc] = shows
        .filter(s => s.name && s.name !== 'Show') // skip empty/placeholder shows
        .map(s => ({
          name: s.name,
          times: s.times && s.times.includes(',') ? s.times.split(',').map(t => t.trim()) : (s.times ? [s.times] : []),
          type: s.type,
          location: s.location || '',
          sara: s.sara || '',
        }))
    }

    window.closeModal('itEntertainmentModal')
    onConfirm(mergedEntertainment)
  }

  window.openModal('itEntertainmentModal')
}

function renderEntParkRows(parkCode) {
  const shows = window._itEntPreviewData?.[parkCode] || []
  return shows.map((show, i) => `
    <tr style="border-top:1px solid var(--border);" data-park="${parkCode}" data-idx="${i}">
      <td style="padding:6px 10px;">
        <input type="text" class="it-ent-name" data-park="${parkCode}" data-idx="${i}"
          value="${show.name}"
          style="font-size:12px;font-weight:500;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);width:100%;box-sizing:border-box;">
        ${show.location ? `<div style="font-size:10px;color:var(--text-3);margin-top:2px;">${show.location}</div>` : ''}
      </td>
      <td style="padding:6px 10px;">
        <input type="text" class="it-ent-time" data-park="${parkCode}" data-idx="${i}"
          value="${show.times}"
          style="font-size:12px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);width:100%;box-sizing:border-box;">
      </td>
      <td style="padding:6px 10px;">
        <span style="display:flex;align-items:center;gap:4px;font-size:10px;color:${SOURCE_COLORS[show.source] || '#666'};">
          <span style="width:6px;height:6px;border-radius:50%;background:${SOURCE_COLORS[show.source] || '#666'};display:inline-block;"></span>
          ${SOURCE_LABELS[show.source] || show.source}
        </span>
      </td>
      <td style="padding:6px 10px;text-align:center;">
        <button onclick="removeEntShow('${parkCode}',${i})" style="background:none;border:none;cursor:pointer;color:var(--red);font-size:12px;padding:2px;" title="Quitar">&#x2715;</button>
      </td>
    </tr>
  `).join('') || `<tr><td colspan="4" style="padding:12px;text-align:center;color:var(--text-3);font-style:italic;">Sin datos de entretenimiento</td></tr>`
}

function reRenderEntParkTable(parkCode) {
  const tables = document.querySelectorAll('#itEntBody table')
  for (const t of tables) {
    const firstRow = t.querySelector('tr[data-park]')
    if (firstRow?.dataset.park === parkCode || (!firstRow && t.querySelector(`[data-park="${parkCode}"]`))) {
      const tbody = t.querySelector('tbody')
      if (tbody) tbody.innerHTML = renderEntParkRows(parkCode)
      return
    }
  }
}

function removeEntShow(parkCode, idx) {
  if (!window._itEntPreviewData?.[parkCode]) return
  window._itEntPreviewData[parkCode].splice(idx, 1)
  reRenderEntParkTable(parkCode)
}

function addEntShow(parkCode) {
  if (!window._itEntPreviewData) window._itEntPreviewData = {}
  if (!window._itEntPreviewData[parkCode]) window._itEntPreviewData[parkCode] = []

  window._itEntPreviewData[parkCode].push({
    name: 'Nuevo show',
    times: '',
    type: 'show',
    source: 'manual',
    sara: '',
    location: '',
  })

  reRenderEntParkTable(parkCode)

  // Focus the name of the newly added row — it'll be the last row
  const shows = window._itEntPreviewData[parkCode]
  const lastIdx = shows.length - 1
  setTimeout(() => {
    const lastRow = document.querySelector(`tr[data-park="${parkCode}"][data-idx="${lastIdx}"]`)
    const timeInput = lastRow?.querySelector('.it-ent-time')
    if (timeInput) { timeInput.focus() }
  }, 50)
}

// ── GENERATION ───────────────────────────────────────────────────────────

async function confirmGenerateDocx(leadId) {
  const lead = findLead(leadId)
  if (!lead) { window.toast('Lead no encontrado', 'error'); return }

  // Collect config from modal BEFORE name prompt (modal still open)
  const selectedParks = [...document.querySelectorAll('.it-park-check:checked')].map(el => el.value)
  if (!selectedParks.length) {
    window.toast('Selecciona al menos un parque', 'error')
    return
  }

  const diningPlanType = document.getElementById('itDiningPlan')?.value || ''
  const hasDining = !!diningPlanType

  // Collect park schedule from modal rows
  const parkSchedule = collectScheduleFromModal(selectedParks)

  // Merge park hours from backend if available
  if (window._itParkHours?.schedule) {
    for (const day of parkSchedule) {
      if (!day.hours) {
        const hoursEntry = window._itParkHours.schedule.find(h =>
          h.park?.toLowerCase().includes(getParkNameForMatch(day.parkCode))
        )
        if (hoursEntry?.hours) {
          day.hours = hoursEntry.hours
        }
      }
    }
  }

  // Hotel classification for early entry / extended hours / Express Pass
  const hotelInfo = classifyHotel(lead)

  // Collect check-in/check-out from modal (Sara can override booking dates)
  const checkInDate = document.getElementById('itCheckInDate')?.value || ''
  const checkOutDate = document.getElementById('itCheckOutDate')?.value || ''

  // Dining reservations
  const diningRows = document.querySelectorAll('#dining-reservations-rows .dining-row')
  const diningReservations = []
  for (const row of diningRows) {
    const restaurant = row.querySelector('.dining-restaurant')?.value?.trim()
    const date = row.querySelector('.dining-date')?.value
    const time = row.querySelector('.dining-time')?.value?.trim()
    const parkCode = row.querySelector('.dining-park')?.value
    if (restaurant) {
      diningReservations.push({ restaurant, date, time, parkCode })
    }
  }

  // LL turn times
  const llRows = document.querySelectorAll('#ll-turn-rows .ll-row')
  const llTurnTimes = []
  for (const row of llRows) {
    const ride = row.querySelector('.ll-ride')?.value?.trim()
    const time = row.querySelector('.ll-time')?.value?.trim()
    const parkCode = row.querySelector('.ll-park')?.value
    if (ride) {
      llTurnTimes.push({ ride, time, parkCode })
    }
  }

  // Evening plans
  const eveningRows = document.querySelectorAll('#evening-plan-rows .evening-row')
  const eveningPlans = []
  for (const row of eveningRows) {
    const date = row.querySelector('.evening-date')?.value
    const plan = row.querySelector('.evening-plan')?.value?.trim()
    if (plan) {
      eveningPlans.push({ date, plan })
    }
  }

  const config = {
    parks: selectedParks,
    hasDining,
    diningPlanType,
    parkSchedule: parkSchedule.length ? parkSchedule : null,
    hotelInfo,
    parkHoursData: window._itParkHours || null,
    closures: window._itParkHours?.closures || null,
    entertainment: window._itParkHours?.entertainment || null,
    entertainmentLive: window._itParkHours?.entertainmentLive || null,
    checkInDate: checkInDate || null,
    checkOutDate: checkOutDate || null,
    diningReservations: diningReservations.length ? diningReservations : null,
    llTurnTimes: llTurnTimes.length ? llTurnTimes : null,
    eveningPlans: eveningPlans.length ? eveningPlans : null,
  }

  window.closeModal('itineraryConfigModal')

  // Show entertainment preview modal for Sara to review/edit before generating
  showEntertainmentPreview(config, lead, (mergedEntertainment) => {
    // Replace entertainment data with Sara's reviewed version
    config.entertainmentLive = mergedEntertainment

    // Prompt for family name, then generate
    promptFamilyName(lead, async (familyName) => {
      config.familyName = familyName
      window.toast('Generando itinerario DOCX...', 'success')

      try {
        const result = await generateItineraryDocx(lead, config)

        // Store version
        if (!lead.formData) lead.formData = {}
        if (!lead.formData._itineraryVersions) lead.formData._itineraryVersions = []
        lead.formData._itineraryVersions.push({
          version: lead.formData._itineraryVersions.length + 1,
          type: 'generated',
          sections: result.sections,
          config,
          createdAt: new Date().toISOString(),
        })

        // Download
        downloadBlob(result.blob, result.filename)

        // Persist to Supabase
        window.syncLeadToSupabase?.(lead)
        window.renderAll?.()

        window.toast(`Itinerario generado: ${result.filename}`, 'success')
      } catch (err) {
        window.toast(`Error generando itinerario: ${err.message}`, 'error')
        console.error('[Itinerary] Generation error:', err)
      }
    })
  })
}

// ── RE-DOWNLOAD ──────────────────────────────────────────────────────────

async function redownloadDocx(leadId) {
  const lead = findLead(leadId)
  if (!lead) return

  const versions = lead.formData?._itineraryVersions || []
  const generated = [...versions].reverse().find(v => v.type === 'generated')
  if (!generated) {
    window.toast('No hay DOCX generado previo', 'error')
    return
  }

  // Always rebuild config from the LATEST data source:
  // 1. If config modal is open → collect from modal inputs
  // 2. If lead has _parkRecommendation → rebuild from recommendation
  // 3. Fallback → use saved config
  let config = generated.config || {}
  const modal = document.getElementById('itineraryConfigModal')
  const isModalOpen = modal?.classList.contains('active')
  let configUpdated = false

  if (isModalOpen) {
    // Modal is open — collect fresh from modal inputs
    const selectedParks = [...document.querySelectorAll('.it-park-check:checked')].map(el => el.value)
    if (selectedParks.length) {
      const parkSchedule = collectScheduleFromModal(selectedParks)
      if (window._itParkHours?.schedule) {
        for (const day of parkSchedule) {
          if (!day.hours) {
            const hoursEntry = window._itParkHours.schedule.find(h =>
              h.park?.toLowerCase().includes(getParkNameForMatch(day.parkCode))
            )
            if (hoursEntry?.hours) day.hours = hoursEntry.hours
          }
        }
      }
      const diningPlanType = document.getElementById('itDiningPlan')?.value || ''
      const hotelInfo = classifyHotel(lead)
      config = {
        parks: selectedParks,
        hasDining: !!diningPlanType,
        diningPlanType,
        parkSchedule: parkSchedule.length ? parkSchedule : null,
        hotelInfo,
        parkHoursData: window._itParkHours || config.parkHoursData || null,
        closures: window._itParkHours?.closures || config.closures || null,
        entertainment: window._itParkHours?.entertainment || config.entertainment || null,
        entertainmentLive: window._itParkHours?.entertainmentLive || config.entertainmentLive || null,
      }
      configUpdated = true
    }
  } else if (lead._parkRecommendation) {
    // Modal is closed but lead has recommendation data — rebuild from it
    const rec = lead._parkRecommendation
    const parkSchedule = buildScheduleFromRecommendation(rec, lead)
    const parks = detectParksFromRecommendation(rec, lead)
    const hotelInfo = classifyHotel(lead)
    config = {
      parks,
      hasDining: config.hasDining ?? false,
      diningPlanType: config.diningPlanType || '',
      parkSchedule: parkSchedule.length ? parkSchedule : null,
      hotelInfo,
      parkHoursData: rec || null,
      closures: rec.closures || null,
      entertainment: rec.entertainment || null,
      entertainmentLive: rec.entertainmentLive || null,
    }
    configUpdated = true
  }

  // Prompt for family name, then regenerate
  promptFamilyName(lead, async (familyName) => {
    config.familyName = familyName
    window.toast('Regenerando DOCX...', 'success')
    try {
      const result = await generateItineraryDocx(lead, config)
      downloadBlob(result.blob, result.filename)

      // Update stored version config so next re-download also has latest
      if (configUpdated) {
        generated.config = config
        window.syncLeadToSupabase?.(lead)
      }
    } catch (err) {
      console.error('[Itinerary] Re-download error:', err)
      window.toast(`Error: ${err.message}`, 'error')
    }
  })
}

// ── PARK HOURS FETCH ─────────────────────────────────────────────────────

async function fetchParkHours(leadId) {
  const lead = findLead(leadId)
  if (!lead?.travelDate) {
    window.toast('El lead necesita fecha de viaje', 'error')
    return
  }

  const statusEl = document.getElementById('itParkHoursStatus')
  statusEl.textContent = 'Consultando horarios...'

  // Detect hotel tier for accurate hours (early entry, extended evening)
  const hotelInfo = classifyHotel(lead)
  const selectedParks = [...document.querySelectorAll('.it-park-check:checked')].map(el => el.value)

  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://mind2magic-api.onrender.com'
    const resp = await fetch(`${apiUrl}/recommend-parks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        startDate: lead.travelDate,
        endDate: lead.travelEnd || lead.travelDate,
        isDisneyGuest: hotelInfo.isDisneyGuest,
        hotel_category: hotelInfo.disneyTier || null,
        parkDays: selectedParks.length || 4,
      }),
    })

    if (resp.ok) {
      const data = await resp.json()
      window._itParkHours = data

      // Fill in hours on schedule rows from the API response
      if (data.parkHours) {
        const schedRows = document.querySelectorAll('.it-schedule-row')
        for (const row of schedRows) {
          const dateInput = row.querySelector('.it-sched-date')
          const parkSelect = row.querySelector('.it-sched-park')
          const hoursInput = row.querySelector('.it-sched-hours')
          const dateVal = dateInput?.value
          const parkCode = parkSelect?.value
          if (dateVal && parkCode && data.parkHours[dateVal]?.[parkCode]) {
            const h = data.parkHours[dateVal][parkCode]
            let hoursStr = `${h.open || '?'} – ${h.close || '?'}`
            if (hotelInfo.isDisneyGuest && h.early_entry) {
              hoursStr = `${h.early_entry} (Early Entry) – ${h.close || '?'}`
            }
            if (hotelInfo.disneyTier === 'deluxe' && h.extended_hours) {
              hoursStr = `${h.early_entry || h.open || '?'} (Early Entry) – ${h.extended_hours} (Extended Evening)`
            }
            hoursInput.value = hoursStr
          }
        }
      }

      const tierNote = hotelInfo.isDisneyGuest
        ? ` (${hotelInfo.disneyTier || 'resort'} guest — Early Entry incluido)`
        : hotelInfo.isUniversalGuest
          ? ` (${hotelInfo.universalTier || 'resort'} guest)`
          : ' (off-site — horario regular)'
      statusEl.textContent = `✅ Horarios obtenidos${tierNote}`
      statusEl.style.color = 'var(--green)'
    } else {
      statusEl.textContent = '⚠️ No se pudieron obtener horarios. Se generará sin ellos.'
      statusEl.style.color = 'var(--yellow)'
    }
  } catch (err) {
    statusEl.textContent = '⚠️ Error de conexión. Se generará sin horarios.'
    statusEl.style.color = 'var(--yellow)'
  }
}

// ── PLACEHOLDER FUNCTIONS (implemented in later modules) ─────────────────

function viewItineraryDiff(leadId) {
  if (window.showDiffModal) {
    window.showDiffModal(leadId)
  } else {
    window.toast('Módulo de comparación no cargado', 'error')
  }
}

// generateFinalPdf, downloadFinalPdf, generateDirectPdf are provided by pdf-finalizer.js (loaded after this module)
// If pdf-finalizer hasn't loaded yet, these fallbacks are used:
function generateFinalPdf(leadId) {
  window.toast('Cargando generador PDF...', 'info')
}

function downloadFinalPdf(leadId) {
  window.toast('Cargando generador PDF...', 'info')
}

function generateDirectPdf(leadId) {
  window.toast('Cargando generador PDF...', 'info')
}

function sendItineraryWhatsApp(leadId) {
  const lead = findLead(leadId)
  if (!lead) return
  const phone = lead.phone || lead.contactoPhone || ''
  if (!phone) { window.toast('Sin número de WhatsApp', 'error'); return }
  const cleanPhone = phone.replace(/\D/g, '')
  const msg = encodeURIComponent(`¡Hola! Te envío tu itinerario personalizado de viaje. 🎢✨`)
  window.open(`https://wa.me/${cleanPhone}?text=${msg}`, '_blank')
}

function showItineraryHistory(leadId) {
  const lead = findLead(leadId)
  if (!lead) return
  const versions = lead.formData?._itineraryVersions || []
  if (!versions.length) { window.toast('Sin historial', 'info'); return }

  const labels = { generated: '📝 DOCX generado', edited: '✏️ DOCX editado', final_pdf: '📄 PDF final' }
  const html = versions.map((v, i) => {
    const date = v.createdAt ? new Date(v.createdAt).toLocaleString('es-CO') : ''
    return `<div style="padding:8px;border-bottom:1px solid var(--border);">
      <span style="font-weight:600;">${labels[v.type] || v.type}</span>
      <span style="font-size:11px;color:var(--text-3);"> — v${v.version} — ${date}</span>
    </div>`
  }).join('')

  // Simple alert-style display
  const modal = document.getElementById('itineraryConfigModal') || (createConfigModal(), document.getElementById('itineraryConfigModal'))
  document.getElementById('itConfigBody').innerHTML = `
    <div style="font-weight:600;margin-bottom:12px;">Historial de Versiones</div>
    ${html}
  `
  window.openModal('itineraryConfigModal')
}

// ── PIPELINE CARD BADGE ──────────────────────────────────────────────────
// Returns badge HTML for pipeline cards

function getItineraryBadge(lead) {
  const versions = lead.formData?._itineraryVersions
  if (!versions?.length) return ''

  const latest = versions[versions.length - 1]
  if (latest.type === 'generated') return '<span style="background:var(--blue);color:#fff;padding:1px 5px;border-radius:3px;font-size:9px;margin-left:4px;">Doc</span>'
  if (latest.type === 'edited') return '<span style="background:var(--yellow);color:#000;padding:1px 5px;border-radius:3px;font-size:9px;margin-left:4px;">Doc</span>'
  if (latest.type === 'final_pdf') return '<span style="background:var(--green);color:#fff;padding:1px 5px;border-radius:3px;font-size:9px;margin-left:4px;">PDF</span>'
  return ''
}

// ── SCHEDULE BUILDER (date + park assignment per day) ─────────────────

function buildScheduleFromLead(lead, parks) {
  const schedule = []
  // Prefer hotel check-in over travelDate — travelDate may be flight arrival (day before check-in)
  const startDateStr = lead.hotels?.[0]?.checkIn || lead.travelDate || ''
  const startDate = startDateStr ? new Date(startDateStr) : null

  // Sara's recommended park order:
  // - Save MK for last (grand finale with fireworks)
  // - HS first (smaller park, good warm-up)
  // - AK second (opens early, closes early)
  // - EP third (World Showcase evening)
  // - MK last (longest hours, best nighttime show)
  const SARA_ORDER = ['hs', 'ak', 'ep', 'mk', 'uso', 'ioa', 'epic']
  const sorted = [...parks].sort((a, b) => {
    const ai = SARA_ORDER.indexOf(a)
    const bi = SARA_ORDER.indexOf(b)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  for (let i = 0; i < sorted.length; i++) {
    let dateStr = ''
    if (startDate) {
      const d = new Date(startDate)
      d.setDate(d.getDate() + i + 1) // +1 to skip check-in day
      dateStr = d.toISOString().split('T')[0] // YYYY-MM-DD for input[type=date]
    }
    schedule.push({ parkCode: sorted[i], date: dateStr, hours: '' })
  }
  return schedule
}

function renderScheduleRows(schedule, allParks) {
  const parkNames = {
    mk: 'Magic Kingdom', hs: 'Hollywood Studios', ep: 'EPCOT', ak: 'Animal Kingdom',
    uso: 'Universal Studios', ioa: 'Islands of Adventure', epic: 'Epic Universe',
  }

  return schedule.map((day, i) => {
    const options = allParks.map(p =>
      `<option value="${p}" ${p === day.parkCode ? 'selected' : ''}>${parkNames[p] || p}</option>`
    ).join('')

    return `<div class="it-schedule-row" style="display:flex;gap:6px;align-items:center;margin-bottom:6px;">
      <input type="date" class="it-sched-date" value="${day.date}" style="font-size:12px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);flex:1;">
      <select class="it-sched-park" style="font-size:12px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);flex:1;">
        ${options}
      </select>
      <input type="text" class="it-sched-hours" value="${day.hours || ''}" placeholder="Horario" style="font-size:11px;padding:4px 6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-2);color:var(--text-1);width:80px;">
      <button onclick="this.parentElement.remove()" style="background:none;border:none;cursor:pointer;color:var(--red);font-size:14px;padding:2px;">✕</button>
    </div>`
  }).join('')
}

function updateItSchedule(leadId) {
  const lead = findLead(leadId)
  if (!lead) return
  const selectedParks = [...document.querySelectorAll('.it-park-check:checked')].map(el => el.value)
  const schedule = buildScheduleFromLead(lead, selectedParks)
  const container = document.getElementById('itScheduleRows')
  if (container) container.innerHTML = renderScheduleRows(schedule, selectedParks)
}

function addItScheduleRow(leadId) {
  const lead = findLead(leadId)
  const selectedParks = [...document.querySelectorAll('.it-park-check:checked')].map(el => el.value)
  if (!selectedParks.length) return

  const container = document.getElementById('itScheduleRows')
  if (!container) return

  // Find last date in schedule to auto-advance
  const existingDates = [...container.querySelectorAll('.it-sched-date')]
  let nextDate = ''
  if (existingDates.length) {
    const lastVal = existingDates[existingDates.length - 1].value
    if (lastVal) {
      const d = new Date(lastVal)
      d.setDate(d.getDate() + 1)
      nextDate = d.toISOString().split('T')[0]
    }
  } else if (lead?.travelDate) {
    const d = new Date(lead.travelDate)
    d.setDate(d.getDate() + 1)
    nextDate = d.toISOString().split('T')[0]
  }

  const newRow = [{ parkCode: selectedParks[0], date: nextDate, hours: '' }]
  container.insertAdjacentHTML('beforeend', renderScheduleRows(newRow, selectedParks))
}

function collectScheduleFromModal(selectedParks) {
  const rows = document.querySelectorAll('.it-schedule-row')
  const schedule = []

  for (const row of rows) {
    const dateInput = row.querySelector('.it-sched-date')
    const parkSelect = row.querySelector('.it-sched-park')
    const hoursInput = row.querySelector('.it-sched-hours')

    const parkCode = parkSelect?.value
    if (!parkCode || !selectedParks.includes(parkCode)) continue

    const dateStr = dateInput?.value || ''
    let dateLabel = ''
    if (dateStr) {
      try {
        const d = new Date(dateStr + 'T12:00:00') // noon to avoid TZ issues
        const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
        const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
        dateLabel = `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`
      } catch { /* ignore */ }
    }

    schedule.push({
      parkCode,
      dateLabel,
      date: dateStr,
      hours: hoursInput?.value?.trim() || '',
    })
  }

  return schedule
}

function getParkNameForMatch(code) {
  const map = {
    mk: 'magic kingdom', hs: 'hollywood', ep: 'epcot', ak: 'animal kingdom',
    uso: 'universal studios', ioa: 'islands', epic: 'epic universe',
  }
  return map[code] || code
}

// ── HELPERS ───────────────────────────────────────────────────────────────

function findLead(id) {
  return store.leads.find(l => l.id === id) || store.historico?.find(l => l.id === id)
}

function hasParks(lead) {
  return lead.parkEntries?.length > 0 || lead.formData?.selectedParks?.length > 0
}

// Build schedule from recommendation data (Días Recomendados)
function buildScheduleFromRecommendation(rec, lead) {
  const schedule = []
  const hotelInfo = classifyHotel(lead)
  const assignments = rec.assignments || {}
  const parkHours = rec.parkHours || {}

  // Sort dates chronologically
  const dates = Object.keys(assignments).sort()
  for (const dateStr of dates) {
    const parkCodes = assignments[dateStr]
    if (!parkCodes?.length) continue
    const parkCode = parkCodes[0] // primary park for that day

    // Build hours string from parkHours
    let hours = ''
    const h = parkHours[dateStr]?.[parkCode]
    if (h) {
      hours = `${h.open || '?'} – ${h.close || '?'}`
      if (hotelInfo.isDisneyGuest && h.early_entry) {
        hours = `${h.early_entry} (Early Entry) – ${h.close || '?'}`
      }
      if (hotelInfo.disneyTier === 'deluxe' && h.extended_hours) {
        hours = `${h.early_entry || h.open || '?'} (Early Entry) – ${h.extended_hours} (Extended Evening)`
      }
    }

    // Build dateLabel for DOCX agenda
    let dateLabel = ''
    try {
      const d = new Date(dateStr + 'T12:00:00')
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
      const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      dateLabel = `${dayNames[d.getDay()]} ${d.getDate()} ${monthNames[d.getMonth()]}`
    } catch { /* ignore */ }

    schedule.push({ parkCode, date: dateStr, dateLabel, hours })
  }

  return schedule
}

// Detect parks from recommendation assignments
function detectParksFromRecommendation(rec, lead) {
  const parkSet = new Set()
  const assignments = rec.assignments || {}
  for (const codes of Object.values(assignments)) {
    for (const c of (codes || [])) parkSet.add(c)
  }

  // Also add parks from lead type/destination that weren't in recommendation
  // (e.g., Universal parks for disney_universal combo)
  const dest = (lead.destination || '').toLowerCase()
  const type = lead.type || ''
  const hasDis = [...parkSet].some(p => ['mk', 'hs', 'ep', 'ak'].includes(p))
  const hasUni = [...parkSet].some(p => ['uso', 'ioa', 'epic'].includes(p))

  if (!hasDis && (type.includes('disney') || dest.includes('disney'))) {
    ;['mk', 'hs', 'ep', 'ak'].forEach(p => parkSet.add(p))
  }
  if (!hasUni && (type.includes('universal') || dest.includes('universal'))) {
    ;['uso', 'ioa', 'epic'].forEach(p => parkSet.add(p))
  }

  return [...parkSet]
}

function detectParksFromLead(lead) {
  const parks = []
  const type = lead.type || ''
  const dest = (lead.destination || '').toLowerCase()

  if (type.includes('disney') || dest.includes('disney')) {
    parks.push('mk', 'hs', 'ep', 'ak')
  }
  if (type.includes('universal') || dest.includes('universal')) {
    parks.push('uso', 'ioa', 'epic')
  }
  if (!parks.length && lead.parkEntries?.length) {
    // Infer from park entries
    for (const pe of lead.parkEntries) {
      const p = (pe.park || '').toLowerCase()
      if (p.includes('disney') && !parks.some(x => ['mk', 'hs', 'ep', 'ak'].includes(x))) {
        parks.push('mk', 'hs', 'ep', 'ak')
      }
      if (p.includes('universal') && !parks.some(x => ['uso', 'ioa', 'epic'].includes(x))) {
        parks.push('uso', 'ioa', 'epic')
      }
    }
  }

  return parks
}

function detectDiningTypeFromLead(lead) {
  const dp = lead.parkEntries?.find(pe => pe.diningPlan)?.diningPlan
  if (dp && typeof dp === 'string') return dp
  if (dp === true) return 'standard' // backward compat: old boolean → standard
  if (lead.formData?.diningPlan) return typeof lead.formData.diningPlan === 'string' ? lead.formData.diningPlan : 'standard'
  return ''
}

// ── WINDOW BRIDGE ────────────────────────────────────────────────────────
// ── DYNAMIC ROW HELPERS (Dining, LL, Evening) ────────────────────────────

window._addDiningRow = function () {
  const container = document.getElementById('dining-reservations-rows')
  if (!container) return
  const row = document.createElement('div')
  row.className = 'dining-row'
  row.style.cssText = 'display: flex; gap: 6px; margin-bottom: 4px; align-items: center;'
  row.innerHTML = `
    <input type="text" placeholder="Restaurante" class="dining-restaurant" style="flex: 2; padding: 4px 6px; font-size: 12px;">
    <input type="date" class="dining-date" style="flex: 1; padding: 4px 6px; font-size: 12px;">
    <input type="text" placeholder="Hora" class="dining-time" style="width: 70px; padding: 4px 6px; font-size: 12px;">
    <select class="dining-park" style="width: 60px; padding: 4px 6px; font-size: 12px;">
      <option value="">Parque</option>
      <option value="mk">MK</option>
      <option value="hs">HS</option>
      <option value="ep">EP</option>
      <option value="ak">AK</option>
      <option value="ds">DS</option>
    </select>
    <button type="button" onclick="this.parentElement.remove()" style="padding: 2px 6px; font-size: 11px; cursor: pointer;">✕</button>
  `
  container.appendChild(row)
}

window._addLLRow = function () {
  const container = document.getElementById('ll-turn-rows')
  if (!container) return
  const row = document.createElement('div')
  row.className = 'll-row'
  row.style.cssText = 'display: flex; gap: 6px; margin-bottom: 4px; align-items: center;'
  row.innerHTML = `
    <input type="text" placeholder="Atraccion" class="ll-ride" style="flex: 2; padding: 4px 6px; font-size: 12px;">
    <input type="text" placeholder="Hora" class="ll-time" style="width: 70px; padding: 4px 6px; font-size: 12px;">
    <select class="ll-park" style="width: 60px; padding: 4px 6px; font-size: 12px;">
      <option value="">Parque</option>
      <option value="mk">MK</option>
      <option value="hs">HS</option>
      <option value="ep">EP</option>
      <option value="ak">AK</option>
    </select>
    <button type="button" onclick="this.parentElement.remove()" style="padding: 2px 6px; font-size: 11px; cursor: pointer;">✕</button>
  `
  container.appendChild(row)
}

window._addEveningRow = function () {
  const container = document.getElementById('evening-plan-rows')
  if (!container) return
  const row = document.createElement('div')
  row.className = 'evening-row'
  row.style.cssText = 'display: flex; gap: 6px; margin-bottom: 4px; align-items: center;'
  row.innerHTML = `
    <input type="date" class="evening-date" style="width: 130px; padding: 4px 6px; font-size: 12px;">
    <input type="text" placeholder="Plan de la noche" class="evening-plan" style="flex: 1; padding: 4px 6px; font-size: 12px;">
    <button type="button" onclick="this.parentElement.remove()" style="padding: 2px 6px; font-size: 11px; cursor: pointer;">✕</button>
  `
  container.appendChild(row)
}

Object.assign(window, {
  renderItinerarySection,
  openItineraryConfig,
  promptFamilyName,
  confirmGenerateDocx,
  redownloadDocx,
  showUploadDocx,
  handleDocxDrop,
  handleDocxSelect,
  viewItineraryDiff,
  generateFinalPdf,
  downloadFinalPdf,
  generateDirectPdf,
  sendItineraryWhatsApp,
  showItineraryHistory,
  getItineraryBadge,
  fetchParkHours,
  updateItSchedule,
  addItScheduleRow,
  removeEntShow,
  addEntShow,
})
